import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";

import DatagridTooltipProvider from "./datagridTooltipProvider.js";
import { useState } from "react";
import { Fragment } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";
import { connect } from "react-redux";

import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import FileDownloadIcon from "@mui/icons-material/NoteAdd";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import {
  mrtGetBodyRowSx,
  mrtGetBottomToolbarSx,
  mrtGetContainerSx,
  mrtGetPaperSx,
  mrtGetTableWrapperSx,
  mrtGetTopToolbarFlexSx,
} from "../../utils/helpers";

import constants from "../../assets/constants";
import { createNewReport } from "../../data/omniDataModel";
import * as AdminSessionActions from "../../state/redux/adminSession";
import {
  capitalizeAllWords,
  createOmniPaginationObject,
  formatIsoDateString,
  getAssessmentTemplateByType,
  getDaysOld,
  getOnboardingLabelFromAssessmentType,
  getReportOnlyFields,
  helperGetIsoDate,
} from "../../utils/helpers";
import AssessmentWizardDialog from "../dialogs/assessmentWizardDialog";
import TextInputDialog from "../dialogs/textInputDialog";
import OmniGridButton from "../ui/omniGridButton";

const calculateColumns = assessmentType => {
  //default case
  let myColumns = [
    {
      accessorKey: "_id",
      header: "ID",
      enableColumnFilterModes: false,
    },
    {
      accessorKey: "omniData.omniType",
      enableColumnFilterModes: false,
      header: "Type",
      size: 160,
      enableSorting: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      enableColumnFilter: false,

      Header: () => (
        <Box
          fx={{
            justifyContent: "start",
            whiteSpace: "nowrap",
          }}
        >
          Types
        </Box>
      ),

      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor: theme.palette.warning.main,
            borderRadius: "0.25rem",
            color: "#fff",
            maxWidth: "16ch",
            minWidth: "16ch",
            p: "0.25rem",
          })}
        >{`${getOnboardingLabelFromAssessmentType(cell.getValue())}`}</Box>
      ),
    },
    {
      // accessorKey: "omniData.completeDate",

      accessorFn: row => `${row.omniData.completeDate}`,
      enableColumnFilterModes: false,
      header: "Completed",
      enableColumnFilter: false,
      enableSorting: false,
      size: 135,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },

      //custom conditional format and styling
      Cell: ({ cell }) => <Box component="span">{`${formatIsoDateString(cell.getValue())}`}</Box>,
    },
    {
      accessorKey: "omniData.completeDate",
      header: "Age",
      size: 222,
      minSize: 222,

      enableColumnFilterModes: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      filterVariant: "range",
      // filterFn: 'betweenInclusive', // default (or between)
      filterFn: "between", // default (or between)

      // could disable just this column's filter

      Cell: ({ cell }) => <Box component="span">{`${getDaysOld(cell.getValue())} days`}</Box>,
    },
  ];

  let assessmentTemplate = getAssessmentTemplateByType(assessmentType);

  for (let index = 0; index < assessmentTemplate.omniData.questions.length; ++index) {
    //if you want to take a special operation for a known shortTitle (like DOB this is the place)
    myColumns.push({
      grow: true,
      enableSorting: false,
      size: 125,
      accessorKey: assessmentTemplate.omniData.questions[index].shortTitle,
      header: capitalizeAllWords(
        assessmentTemplate.omniData.questions[index].shortTitle
          .replace("score", "")
          .replace(/([A-Z]+)/g, " $1"),
      ),

      muiTableHeadCellProps: {
        align: "left",
      },
      Cell: ({ row }) => (
        <Box component="span" sx={{}}>{`${
          index < row.original.omniData.questions.length
            ? row.original.omniData.questions[index].answerValue
            : "Error"
        }`}</Box>
      ),
      enableColumnFilter: false,
      enableColumnFilterModes: false,
    });
  }

  return myColumns;
};

const getAssessmentShortTitles = assessmentType => {
  //default case
  let shortTitles = [];
  let assessmentTemplate = getAssessmentTemplateByType(assessmentType);
  for (let index = 0; index < assessmentTemplate.omniData.questions.length; ++index) {
    shortTitles.push(assessmentTemplate.omniData.questions[index].shortTitle);
  }
  return shortTitles;
};

const getInitialState = userGroup => {
  return {
    density: "compact",
    columnVisibility: {
      _id: false,
      "omniData.givenName": true,
      "omniData.familyName": true,
      "omniData.mobilePhone": false,
      "omniData.email": true,
    },
  };
};

export const SurveyAssessments = props => {
  const {
    assessmentODMs,
    onRequestNewPage = null,
    isLoading = false,
    assessmentPagination = createOmniPaginationObject,
    assessmentPaginationFocus = constants.assessmentIDs.omniHealthHistory,
  } = props;

  const theme = useTheme();
  const [summaryAssessment, setSummaryAssessment] = useState(null);
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  const columns = useMemo(
    () => calculateColumns(assessmentPaginationFocus),
    [assessmentPaginationFocus],
  );
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(0);

  const [showReportNameInput, setShowReportNameInput] = useState(null);

  useEffect(() => {
    if (assessmentPagination != null) {
      setPagination({
        pageIndex: assessmentPagination.pageIndex - 1,
        pageSize: assessmentPagination.pageSize,
      });
      setRowCount(assessmentPagination.totalItems);
    }
  }, [assessmentPagination]);

  const [sorting, setSorting] = useState([]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    let newQuery = {};

    columnFilters.forEach((element, index) => {
      // if (element.id === "omniAudit.createdDate") {
      if (element.id === "omniData.completionDate") {
        const min = parseInt(element.value[0]);
        const max = parseInt(element.value[1]);

        //"2024-02-09T16:23:46.385Z"
        // const gteTime = "T00:00:00.000000Z"
        // const lteTime = "T24:00:00.000000Z"

        if (!isNaN(min) && !isNaN(max)) {
          let minString = helperGetIsoDate(min);
          let maxString = helperGetIsoDate(max);
          newQuery[element.id] = {
            "#gte": maxString.substring(0, 10),
            "#lte": minString.substring(0, 10),
          };
        } else if (!isNaN(min)) {
          let theDateSring = helperGetIsoDate(min);
          newQuery[element.id] = {
            "#lte": theDateSring.substring(0, 10),
          };
        } else if (!isNaN(max)) {
          let theDateSring = helperGetIsoDate(max);
          newQuery[element.id] = {
            "#gte": theDateSring.substring(0, 10),
          };
        }
      } else {
        newQuery[element.id] = {
          "#regex": `${element.value}`,
          "#options": "i",
        };
      }
    });

    let newSorting = {};
    sorting.forEach(element => {
      newSorting[element.id] = element.desc ? -1 : 1;
    });

    if (
      assessmentPagination &&
      (pagination.pageIndex + 1 !== assessmentPagination.pageIndex ||
        pagination.pageSize !== assessmentPagination.pageSize ||
        JSON.stringify(pagination.query) !== JSON.stringify(columnFilters))
    ) {
      let newPage = createOmniPaginationObject(assessmentPagination);
      newPage.pageIndex = pagination.pageIndex + 1;
      newPage.pageSize = pagination.pageSize;
      newPage.query = newQuery;
      newPage.sort = newSorting;

      onRequestNewPage(newPage);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  if (assessmentODMs == null) {
    return <Fragment></Fragment>;
  }

  return (
    <DatagridTooltipProvider>
      <Box sx={mrtGetTableWrapperSx(0, !assessmentODMs?.length)}>
      <MaterialReactTable
      layoutMode="grid"
      muiTableBodyRowProps={{ sx: mrtGetBodyRowSx() }}
      columns={columns}
      data={assessmentODMs}
      initialState={initialState}
      defaultColumn={{
        minSize: 20, //allow columns to get smaller than default
        maxSize: 9001, //allow columns to get larger than default
        size: 100, //make columns wider by default
        grow: false,
      }}
      state={{
        columnFilters,
        isLoading,
        pagination,
        sorting,
      }} //pass our managed row selection state to the table to use
      //pagination
      manualPagination={true}
      onColumnFiltersChange={setColumnFilters}
      onPaginationChange={setPagination}
      manualSorting
      manualFiltering
      onSortingChange={setSorting}
      //header
      enableColumnActions={false}
      enableStickyHeader={true}
      enableColumnFilterModes={true}
      filterFns={{
        customFilterFn: (row, id, filterValue) => {
          return row.getValue(id) === filterValue;
        },
      }}
      muiTableHeadCellFilterTextFieldProps={{
        sx: {
          "& .MuiInputBase-input": { padding: "7px" },
          "& .MuiInputBase-root": { paddingRight: "0px" },
          "& .MuiSelect-iconOutlined": { marginRight: "0.75rem" },
          "& .MuiSelect-outlined": { paddingRight: "0px" },

          width: "80%",
        },
        variant: "outlined",
      }}
      //row
      enableRowActions={true}
      enableRowSelection={false}
      getRowId={originalRow => originalRow._id}
      rowCount={rowCount}
      positionToolbarAlertBanner="bottom"
      muiTablePaginationProps={{
        rowsPerPageOptions: [5, 10, 20],
      }}
      renderTopToolbarCustomActions={() => <Box sx={{ minHeight: "40px" }} />}
      muiTopToolbarProps={{
        sx: {
          ...mrtGetTopToolbarFlexSx(),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
        },
      }}
      muiBottomToolbarProps={{
        sx: mrtGetBottomToolbarSx(theme),
      }}
      muiTablePaperProps={{
        sx: mrtGetPaperSx(),
      }}
      muiTableContainerProps={{
        ref: tableContainerRef, //get access to the table container element
        sx: mrtGetContainerSx(theme, mobileLayout),
      }}
      muiTableProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.commonground.main,
        },
      }}
      muiTableHeadCellProps={{
        sx: {
          backgroundColor: alpha(theme.palette.commonground.contrastText, 0.25),
          color: theme.palette.commonground.contrastText,
          justifyContent: "start",

          textOverflow: "",
          overflowWrap: "break-word",
          padding: 0,
          // inlineSize: "min-content",
        },
      }}
      renderRowActions={({ row }) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: "2px",
            padding: 0,
          }}
        >
          <OmniGridButton
            key="edit"
            onClick={() => {
              setSummaryAssessment(row.original);
            }}
            icon={<OpenInNewRounded fontSize="small" />}
          />
        </Box>
      )}
      renderToolbarInternalActions={({ table }) => (
        <Box>
          {sorting.length > 0 && (
            <Tooltip title="Clear All Sorting" arrow>
              <IconButton
                sx={{ color: theme.palette.secondary.main }}
                onClick={() => {
                  setSorting([]);
                }}
              >
                <ClearSortingIcon />
              </IconButton>
            </Tooltip>
          )}
          {columnFilters.length > 0 && (
            <Tooltip title="Clear All Filters" arrow>
              <IconButton
                sx={{ color: theme.palette.secondary.main }}
                onClick={() => {
                  setColumnFilters([]);
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* eslint-disable react/jsx-pascal-case*/}
          {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
          {/* eslint-enablereact/jsx-pascal-case */}
          {summaryAssessment && (
            <AssessmentWizardDialog
              open={summaryAssessment != null}
              displayResultsAssessmentODM={summaryAssessment}
              onClose={() => {
                setSummaryAssessment(null);
              }}
            />
          )}

          <Tooltip title="Export Via Report" arrow>
            <IconButton
              onClick={() => {
                setShowReportNameInput(
                  `${new Date().toJSON().slice(0, 10)}-Assessments-${assessmentPaginationFocus}-`,
                );
              }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          {showReportNameInput && (
            <TextInputDialog
              dialogTitle="Report Name"
              dialogMessage={"Please name your report"}
              inputLabel={`${"Report Name"}`}
              defaultValue={showReportNameInput}
              open={showReportNameInput}
              onAccept={theText => {
                /// do it!
                //create the report ..

                let theReport = createNewReport();
                theReport.omniData.name = theText;
                theReport.omniData.reportType = constants.reportTypeEnums.assessments;

                theReport.omniData["onlyFields"] = getReportOnlyFields(
                  constants.reportTypeEnums.assessments,
                  false,
                  assessmentPaginationFocus,
                );

                theReport.omniData["questions"] =
                  getAssessmentShortTitles(assessmentPaginationFocus);

                let theQuery = assessmentPagination.query;

                theReport.omniData["query"] = theQuery;
                delete theReport.omniData.dateBefore;
                delete theReport.omniData.dateAfter;

                props.createReportRequest(theReport);

                setShowReportNameInput(false);
              }}
              onClose={() => {
                setShowReportNameInput(false);
              }}
            />
          )}
        </Box>
      )}
      />
      </Box>
    </DatagridTooltipProvider>
  );
};

const mapStateToProps = state => ({});

const mapDispatchToProps = {
  createReportRequest: AdminSessionActions.createReportRequest,
};
export default connect(mapStateToProps, mapDispatchToProps)(SurveyAssessments);
